import type {
  DynamicFieldValue,
  DynamicFieldValues,
  EmrCodeDynamicField,
  EmrCodeFieldBindings,
  EmrCodeFieldType,
} from 'features/Procedure/state/procedure-api.type'

const OPTION_FIELD_TYPES: EmrCodeFieldType[] = [
  'dropdown',
  'multiselect',
  'checkboxgroup',
  'radiogroup',
]

const SINGLE_OPTION_FIELD_TYPES: EmrCodeFieldType[] = ['dropdown', 'radiogroup']

export type DynamicFieldOption = { value: string; label: string }

export type DynamicFieldsDto = Record<string, string | number | string[] | null>

export const isArrayBindings = (bindings?: EmrCodeFieldBindings): bindings is string[] =>
  Array.isArray(bindings)

const getBindingsLength = (bindings?: EmrCodeFieldBindings): number => {
  if (!bindings) {
    return 0
  }
  return isArrayBindings(bindings) ? bindings.length : Object.keys(bindings).length
}

// Array bindings use the value as both option value and label; object bindings map key -> label,
// where the key is the value submitted back to the API.
export const getBindingOptions = (bindings?: EmrCodeFieldBindings): DynamicFieldOption[] => {
  if (!bindings) {
    return []
  }
  if (isArrayBindings(bindings)) {
    return bindings.map((binding) => ({ value: binding, label: binding }))
  }
  return Object.entries(bindings).map(([value, label]) => ({ value, label }))
}

export const isRenderableField = (field: EmrCodeDynamicField): boolean =>
  !OPTION_FIELD_TYPES.includes(field.type) || getBindingsLength(field.bindings) > 0

export const isDynamicFieldValueEmpty = (value: DynamicFieldValue): boolean => {
  if (value === undefined || value === null) {
    return true
  }
  if (typeof value === 'string') {
    return value.trim() === ''
  }
  if (Array.isArray(value)) {
    return value.length === 0
  }
  return false
}

export const getMissingRequiredDynamicFields = (
  fields: EmrCodeDynamicField[] | undefined,
  values: DynamicFieldValues | undefined,
): EmrCodeDynamicField[] =>
  (fields ?? [])
    .filter(isRenderableField)
    .filter((field) => field.required && isDynamicFieldValueEmpty(values?.[field.name]))

// Array bindings submit an array of the selected values (single selects are wrapped too);
// object bindings (and non-option fields) submit the value as-is, defaulting to null.
// Integer fields are submitted as strings: the backend stores these columns as strings and
// rejects raw numbers (numeric-string values still satisfy its numeric validation rules).
const buildFieldSubmitValue = (
  field: EmrCodeDynamicField,
  rawValue: DynamicFieldValue,
): DynamicFieldsDto[string] => {
  if (isArrayBindings(field.bindings)) {
    if (Array.isArray(rawValue)) {
      return rawValue
    }
    if (rawValue === undefined || rawValue === null || rawValue === '') {
      return null
    }
    return [String(rawValue)]
  }
  if (field.type === 'integer') {
    return rawValue === undefined || rawValue === null || rawValue === '' ? null : String(rawValue)
  }
  return rawValue ?? null
}

export const buildDynamicFieldsDto = (
  fields: EmrCodeDynamicField[] | undefined,
  values: DynamicFieldValues | undefined,
): DynamicFieldsDto =>
  Object.fromEntries(
    (fields ?? [])
      .filter(isRenderableField)
      .map((field) => [field.name, buildFieldSubmitValue(field, values?.[field.name])]),
  )

const MULTI_VALUE_FIELD_TYPES: EmrCodeFieldType[] = ['multiselect', 'checkboxgroup']

export const tryParseJson = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value
  }
  try {
    return JSON.parse(value) as unknown
  } catch {
    return value
  }
}

export const toIntegerValue = (rawValue: unknown): number | undefined => {
  const numericValue = typeof rawValue === 'number' ? rawValue : Number(rawValue)
  return Number.isFinite(numericValue) ? Math.trunc(numericValue) : undefined
}

const normalizeSourceValue = (field: EmrCodeDynamicField, rawValue: unknown): DynamicFieldValue => {
  if (MULTI_VALUE_FIELD_TYPES.includes(field.type)) {
    const parsedValue = tryParseJson(rawValue)
    return Array.isArray(parsedValue) ? parsedValue.map(String) : [String(parsedValue)]
  }
  if (field.type === 'integer') {
    return toIntegerValue(rawValue)
  }
  // Single-select option fields with array bindings are submitted (and echoed back) as arrays,
  // so unwrap the first value to keep the control single-valued.
  if (SINGLE_OPTION_FIELD_TYPES.includes(field.type)) {
    const parsedValue = tryParseJson(rawValue)
    if (Array.isArray(parsedValue)) {
      return parsedValue.length ? String(parsedValue[0]) : undefined
    }
    return String(parsedValue)
  }
  if (typeof rawValue === 'number') {
    return rawValue
  }
  return String(rawValue)
}

export const extractDynamicFieldValues = (
  fields: EmrCodeDynamicField[] | undefined,
  source: Record<string, unknown>,
): DynamicFieldValues =>
  Object.fromEntries(
    (fields ?? [])
      .filter(isRenderableField)
      .map((field) => ({ field, rawValue: source[field.name] }))
      .filter(({ rawValue }) => rawValue !== undefined && rawValue !== null && rawValue !== '')
      .map(({ field, rawValue }) => [field.name, normalizeSourceValue(field, rawValue)])
      .filter(([, value]) => value !== undefined),
  )

export const getNonIntegerDynamicFields = (
  fields: EmrCodeDynamicField[] | undefined,
  values: DynamicFieldValues | undefined,
): EmrCodeDynamicField[] =>
  (fields ?? [])
    .filter(isRenderableField)
    .filter((field) => field.type === 'integer')
    .filter((field) => {
      const value = values?.[field.name]
      if (isDynamicFieldValueEmpty(value)) {
        return false
      }
      return !Number.isInteger(typeof value === 'number' ? value : Number(value))
    })

export const STRING_FIELD_MAX_LENGTH = 255

export const getTooLongStringDynamicFields = (
  fields: EmrCodeDynamicField[] | undefined,
  values: DynamicFieldValues | undefined,
): EmrCodeDynamicField[] =>
  (fields ?? [])
    .filter(isRenderableField)
    .filter((field) => field.type === 'string')
    .filter((field) => {
      const value = values?.[field.name]
      return typeof value === 'string' && value.length > STRING_FIELD_MAX_LENGTH
    })
